import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface AuthContextType {
  isAuthenticated: boolean;
  username: string | null;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Load credentials from environment variable
// Format: JSON string like '{"username1":"password1","username2":"password2"}'
const getAuthCredentials = (): Record<string, string> => {
  try {
    const credentialsEnv = process.env.REACT_APP_AUTH_CREDENTIALS;
    if (!credentialsEnv) {
      console.warn('REACT_APP_AUTH_CREDENTIALS environment variable is not set');
      return {};
    }
    const credentials = JSON.parse(credentialsEnv);
    if (typeof credentials !== 'object' || credentials === null || Array.isArray(credentials)) {
      console.error('REACT_APP_AUTH_CREDENTIALS must be a valid JSON object');
      return {};
    }
    return credentials;
  } catch (error) {
    console.error('Failed to parse REACT_APP_AUTH_CREDENTIALS:', error);
    return {};
  }
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [username, setUsername] = useState<string | null>(null);
  const [authCredentials] = useState<Record<string, string>>(getAuthCredentials());

  useEffect(() => {
    // Check if user is already logged in (from localStorage)
    const storedAuth = localStorage.getItem('isAuthenticated');
    const storedUsername = localStorage.getItem('username');
    if (storedAuth === 'true' && storedUsername) {
      setIsAuthenticated(true);
      setUsername(storedUsername);
    }
  }, []);

  const login = async (username: string, password: string): Promise<boolean> => {
    // Only authenticate against credentials from environment variable
    // No fallback credentials for security
    if (Object.keys(authCredentials).length === 0) {
      console.error('No authentication credentials configured. Please set REACT_APP_AUTH_CREDENTIALS environment variable.');
      return false;
    }

    if (authCredentials[username] && authCredentials[username] === password) {
      setIsAuthenticated(true);
      setUsername(username);
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('username', username);
      return true;
    }
    
    return false;
  };

  const logout = () => {
    setIsAuthenticated(false);
    setUsername(null);
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('username');
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, username, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

